import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import { Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { StatStrip } from "../../../components/UIToolkit/molecules/StatStrip";
import type { PbjPlanCost } from "../../../utils/models";
import { formatCurrency } from "./planValue.utils";

type Props = {
	cost: PbjPlanCost;
};

export const PlanCostPanel: FC<Props> = ({ cost }) => {
	const { t } = useTranslation();
	// The bar measures spend against the WHOLE commitment; the budget-to-date marker below it is
	// what `onBudget` is actually judged on, so both are shown rather than collapsing to one.
	const spentPct = cost.totalPrice > 0 ? Math.min(100, Math.max(0, (cost.spendSoFar / cost.totalPrice) * 100)) : 0;
	// `spendSoFar` is `max(0, delivered) x wage`, so zero spend means the department has delivered
	// nothing or gone backwards. Budget adherence carries no information in that state: `0 <=
	// budgetToDate` is trivially true, so a green "On budget" would be reporting the ABSENCE of
	// delivery as financial health — and the worse a plan goes, the healthier this panel reads. The
	// verdict is withheld until there is spend to judge.
	const hasSpend = cost.spendSoFar > 0;
	// One decision, three consistent outputs — tone, icon and copy cannot drift out of step the way
	// three separate conditionals on the same predicate can.
	const budgetBadge = !hasSpend
		? { tone: "neutral" as const, icon: <RemoveCircleOutlineIcon />, labelKey: "pbj_model.tracker.no_spend_yet" }
		: cost.onBudget
			? { tone: "success" as const, icon: <CheckCircleOutlineIcon />, labelKey: "pbj_model.tracker.on_budget" }
			: { tone: "error" as const, icon: <ErrorOutlineIcon />, labelKey: "pbj_model.tracker.over_budget" };

	return (
		<Paper
			variant="outlined"
			sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
			data-testid="pbj-model-plan-cost"
		>
			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: 1 }}>
				<Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
					{t("pbj_model.tracker.investment")}
				</Typography>
				<MetaChip tone={budgetBadge.tone} icon={budgetBadge.icon} label={t(budgetBadge.labelKey)} dataTestId="pbj-model-plan-on-budget" />
			</Stack>

			<StatStrip
				dataTestId="pbj-model-plan-cost-stats"
				items={[
					{ label: t("pbj_model.tracker.total_price"), value: formatCurrency(cost.totalPrice), dataTestId: "pbj-model-plan-total-price" },
					{ label: t("pbj_model.tracker.spend_so_far"), value: formatCurrency(cost.spendSoFar), dataTestId: "pbj-model-plan-spend-so-far" },
				]}
			/>

			<Box sx={{ mt: "auto" }}>
				{/* A determinate bar still paints its TRACK, and MUI tints that track with the bar's own
				    colour. At this height a success-tinted track behind a zero-width fill is
				    indistinguishable from a bar filled to 100% — it read as "fully spent" on a plan that
				    has spent nothing. Neutralising the track until there is spend keeps an empty bar
				    looking empty. */}
				<LinearProgress
					variant="determinate"
					value={spentPct}
					color={hasSpend && !cost.onBudget ? "error" : "success"}
					sx={{ height: 8, borderRadius: 4, ...(hasSpend ? {} : { bgcolor: "action.disabledBackground" }) }}
					aria-label={t("pbj_model.tracker.spend_progress_aria")}
					data-testid="pbj-model-plan-spend-bar"
				/>
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
					{t("pbj_model.tracker.budget_to_date", {
						budget: formatCurrency(cost.budgetToDate),
						share: (cost.elapsedShare * 100).toFixed(0),
					})}
				</Typography>
				{/* What the figure actually means. Spend is `deliveredToDate x the plan's frozen wage` —
				    the DAY-granular delivery, the same one the progress bar fills to, so the two always
				    agree. Keyed off `hasSpend` rather than off elapsed weeks: zero spend means nothing
				    was delivered above baseline, which is a statement about the department, not about
				    the calendar, and saying "you have not spent" is not the same as "we cannot tell you
				    yet". */}
				<Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }} data-testid="pbj-model-plan-spend-basis">
					{t(hasSpend ? "pbj_model.tracker.spend_basis" : "pbj_model.tracker.spend_basis_none")}
				</Typography>
			</Box>
		</Paper>
	);
};
